<script lang="ts" setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '@/store/user'
import { usePayrollStore } from '@/store/payroll-data'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { useField, useForm } from 'vee-validate'
import { object, array, date } from 'yup'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import {
  DCard,
  DBtn,
  DTextField,
  DMultiselect,
  DModal,
  DLoading,
  DEmployeesFilter,
  FilterLinesIcon,
  Users01Icon
} from 'front-library'
import {
  convertArrayToString,
  ReportBonus,
  getPayroll,
  getEmployeesReport,
  getConcepts,
  getReportRequest
} from '@/app/core/services/reports'
import { generateIRPF } from '@/app/core/services/reports/banks'
usePayrollChange(() => back())

/* store, router, notifications */
const storePayroll = usePayrollStore()
const store = useUserStore()
const router = useRouter()
const route = useRoute()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Datos */
const itemsPayrolls = ref<any[]>([])
const itemsTaxed = ref<any[]>([])
const allIdsTaxedSel = ref<any[]>([])
const itemsDeductions = ref<any[]>([])
const allIdsDctsSel = ref<any[]>([])
const itemsWithholdings = ref<any[]>([])
const allIdsWithholdingsSel = ref<any[]>([])
const itemsBonus = ref<any[]>([])
const allIdsBonusSel = ref<any[]>([])
const itemsVacation = ref<any[]>([])
const allIdsVacationSel = ref<any[]>([])
const allConcepts = ref<any[]>([])
const itemsEmployees = ref<string[]>([])
const urlFile = ref(null)
const refresh = ref()

/* Esquema de validacion campos obligatorios y no obligatorios */
const validationSchema = object().shape({
  payroll: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  startDate: date().required(t('form.valid.required')),
  endDate: date().when('startDate', (startDate: any, schema) => {
    if (startDate[0] !== undefined) {
      return schema
        .min(
          startDate,
          `${t('form.valid.startDateMin')} ${dayjs(startDate).format(
            'DD/MM/YYYY'
          )}`
        )
        .required(t('form.valid.required'))
        .test('same-year', t('form.valid.sameYear'), function (endDate) {
          const startYear = dayjs(startDate).year()
          const endYear = dayjs(endDate).year()
          return startYear === endYear
        })
    }
    return schema.required(t('form.valid.required'))
  }),
  taxedTotals: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  proportionalDeductions: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  withholdingsIRPF: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  bonuses: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  vacationSalary: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  employees: array()
    .min(1, t('form.valid.employeeSelectionRequired'))
    .required(t('form.valid.fieldRequired', { field: t('fields.employees') }))
})

/* Uso del metodo de validación, retorno de errores y metodo para reiniciar formulario */
const { handleSubmit, errors } = useForm<any>({
  initialValues: {},
  validationSchema
})

/* Definicion campos del formulario */
const { value: payroll } = useField<string[]>(ref('payroll'))
const { value: startDate } = useField<string | undefined>('startDate')
const { value: endDate } = useField<string | undefined>('endDate')
const { value: taxedTotals } = useField<string[]>(ref('taxedTotals'))
const { value: proportionalDeductions } = useField<string[]>(
  ref('proportionalDeductions')
)
const { value: withholdingsIRPF } = useField<string[]>(ref('withholdingsIRPF'))
const { value: bonuses } = useField<string[]>(ref('bonuses'))
const { value: vacationSalary } = useField<string[]>(ref('vacationSalary'))
const { value: employees } = useField<any[]>(ref('employees'))
const { value: format } = useField<string>(ref('format'))

/* Metodo que valida los campos obligatorios del formulario */
const onSubmit = handleSubmit((values: any) => {
  // void saveFilters(values)
  values.taxedTotals = allIdsTaxedSel.value
  values.proportionalDeductions = allIdsDctsSel.value
  values.withholdingsIRPF = allIdsWithholdingsSel.value
  values.bonuses = allIdsBonusSel.value
  values.vacationSalary = allIdsVacationSel.value
  const path = route.params.code as string
  const decodeCode = atob(path)
  const createReport: ReportBonus = {
    code: decodeCode,
    parameters: values
  }
  void generateReport(createReport)
})

/* Crear solicitud del reporte */
const generateReport = async (values: any) => {
  loading.value = true
  messageLoading.value = t('loading.report')
  try {
    const response = await generateIRPF(values)
    reportRequest(response)
  } catch (error: any) {
    showError(error)
  }
}

/* Mostrar estado del la solicitud del reporte generado  */
const reportRequest = (id: string) => {
  urlFile.value = null
  try {
    refresh.value = setInterval(async () => {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        if (response.errors.length === 1) {
          descriptionModal.value = response.errors[0]
        } else {
          for (let i = 0; i < response.errors.length; i++) {
            const error = response.errors[i]
            descriptionModal.value += error
            if (i < response.errors.length - 1) descriptionModal.value += '<br>'
          }
        }
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    }, 5000)
  } catch (error: any) {
    showError(error)
  }
}

/* Guardar filtros del reporte */
const saveFilters = (values: any) => {
  const filters = {
    ...values,
    user: store.user?.id
  }
  let verifyUser = storePayroll.IRPF.some(
    (obj: any) => obj.user === filters.user
  )
  if (!verifyUser) storePayroll.IRPF.push(filters)
  else {
    storePayroll.IRPF = storePayroll.IRPF.map((obj: any) => {
      if (obj.user === filters.user) return filters
      return obj
    })
  }
}

/* Listar empleados */
const getListEmployees = async () => {
  loading.value = true
  messageLoading.value = t('loading.employee')
  try {
    if (payroll.value.length !== 0) {
      const strPayrolls = await convertArrayToString(payroll.value)
      itemsEmployees.value = await getEmployeesReport(strPayrolls, ``)
      employees.value = itemsEmployees.value.map((_: any) => _.id)
    } else {
      notifications.addNotification({
        title: t('reports.notification.noEmployeesFound'),
        text: t('reports.notification.noEmployees'),
        type: 'warning',
        time: 10000
      })
    }
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
}

/* Listar conceptos */
const listConcepts = async () => {
  loading.value = true
  messageLoading.value = t('loading.concepts')
  itemsTaxed.value = []
  itemsDeductions.value = []
  itemsWithholdings.value = []
  itemsBonus.value = []
  itemsVacation.value = []
  try {
    if (payroll.value?.length > 0) {
      const strPayroll = await convertArrayToString(payroll.value)
      allConcepts.value = await getConcepts(strPayroll, 'irpf')
      if (taxedTotals.value?.length > 0) filterCodeTaxed()
      if (proportionalDeductions.value?.length > 0) filterCodeDeductions()
      if (withholdingsIRPF.value?.length > 0) filterCodeWithholdings()
      if (bonuses.value?.length > 0) filterCodeBonus()
      if (vacationSalary.value?.length > 0) filterCodeVacation()
      itemsTaxed.value = uniqueByKey(allConcepts.value)
      itemsDeductions.value = uniqueByKey(allConcepts.value)
      itemsWithholdings.value = uniqueByKey(allConcepts.value)
      itemsBonus.value = uniqueByKey(allConcepts.value)
      itemsVacation.value = uniqueByKey(allConcepts.value)
      if (
        itemsTaxed.value.length === 0 ||
        itemsDeductions.value.length === 0 ||
        itemsWithholdings.value.length === 0 ||
        itemsBonus.value.length === 0 ||
        itemsVacation.value.length === 0
      ) {
        notifications.addNotification({
          title: t('reports.notification.noConceptsFound'),
          text: t('reports.notification.noConcepts'),
          type: 'warning',
          time: 10000
        })
      }
    }
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
}
function uniqueByKey(allItems: any) {
  return [
    ...new Map(allItems.map((item: any) => [item['code'], item])).values()
  ]
}

/* Filtrar codigos de conceptos */
const filterByCode = async (
  sourceArray: any,
  targetArray: any,
  filterArray: any
) => {
  targetArray.value = sourceArray
    .filter((item: any) => filterArray.value.indexOf(item.code) > -1)
    .map((item: any) => item.id)
}

const filterCodeTaxed = async () => {
  await filterByCode(allConcepts.value, allIdsTaxedSel, taxedTotals)
}
const filterCodeDeductions = async () => {
  await filterByCode(allConcepts.value, allIdsDctsSel, proportionalDeductions)
}
const filterCodeWithholdings = async () => {
  await filterByCode(allConcepts.value, allIdsWithholdingsSel, withholdingsIRPF)
}
const filterCodeBonus = async () => {
  await filterByCode(allConcepts.value, allIdsBonusSel, bonuses)
}
const filterCodeVacation = async () => {
  await filterByCode(allConcepts.value, allIdsVacationSel, vacationSalary)
}

/* Mostrar notificación de error */
const showError = (error: any) => {
  loading.value = false
  notifications.addNotification({
    title: error.message,
    text: error.response.data.message,
    type: 'error',
    time: 10000
  })
}

/* Validar campos de fechas (no dejar vacios) */
const validateDate = () => {
  if (startDate.value === '') startDate.value = undefined
  if (endDate.value === '') endDate.value = undefined
}

/* Cerrar modal */
const closeModal = () => {
  showModal.value = false
}

/* Volver a la lista de reportes */
const back = () => {
  void router.push({ name: 'MainReports' })
}

onMounted(async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    itemsPayrolls.value = await getPayroll(store.user?.id as string)
    payroll.value = itemsPayrolls.value
      .filter((_: any) => _.id === storePayroll.payroll?.id)
      .map((_: any) => _.id)
    await listConcepts()
    await getListEmployees()
    // // cargar filtros de compañia
    // TODO: Validar implementacion de filtros
    // const filters = await storePayroll.IRPF.find(
    //   (obj: any) => obj.user === store.user?.id
    // )
    // if (filters) {
    //   bonuses.value = filters.bonuses
    //   proportionalDeductions.value = filters.proportionalDeductions
    //   taxedTotals.value = filters.taxedTotals
    //   vacationSalary.value = filters.vacationSalary
    //   withholdingsIRPF.value = filters.withholdingsIRPF
    //   employees.value = filters.employees
    //   endDate.value = filters.endDate
    //   payroll.value = filters.payroll
    //   startDate.value = filters.startDate
    //   filterCodeTaxed()
    //   filterCodeDeductions()
    //   filterCodeWithholdings()
    //   filterCodeBonus()
    //   filterCodeVacation()
    // }
  } catch (error: any) {
    showError(error)
  }
})

onUnmounted(() => {
  clearInterval(refresh.value)
})
</script>
<template>
  <div class="h-full px-5 pb-5">
    <DLoading v-show="loading" :message="messageLoading" />
    <DModal
      :open="showModal"
      :typeAlert="typeAlertModal"
      :title="titleModal"
      :description="descriptionModal"
      @closeModal="closeModal">
      <template v-slot:description v-if="urlFile != null">
        <a
          class="underline tracking-widest text-blue-600"
          :href="urlFile"
          target="_blank"
          v-html="t('reports.notification.viewFile')" />
      </template>
    </DModal>
    <div class="bg-gray-50 sm:pb-5 lg:pb-12">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="t('reports.irpf')" />
      </div>
    </div>
    <form class="scroll overflow-auto" style="height: calc(100% - 10rem)">
      <div class="flex flex-col gap-4">
        <DCard :footer="true">
          <div class="grid grid-cols-4">
            <div class="col-span-4 lg:col-span-3 sm:pb-5 lg:pb-0 px-3">
              <div class="grid grid-cols-6 gap-4">
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.payrolls')"
                    v-model="payroll"
                    :headerTitle="t('reports.fields.payrolls')"
                    :required="true"
                    :options="itemsPayrolls"
                    :error="errors.payroll"
                    @change="getListEmployees, listConcepts" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('reports.fields.startDate')"
                    v-model="startDate"
                    :required="true"
                    :error="errors.startDate"
                    @input="validateDate" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('reports.fields.endDate')"
                    v-model="endDate"
                    :required="true"
                    :error="errors.endDate"
                    @input="validateDate" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.conceptsTotalTaxed')"
                    v-model="taxedTotals"
                    value-key="code"
                    :headerTitle="t('reports.fields.conceptsTotalTaxed')"
                    :required="true"
                    :options="itemsTaxed"
                    :error="errors.taxedTotals"
                    @change="filterCodeTaxed()" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.conceptsProportionalDeductions')"
                    v-model="proportionalDeductions"
                    value-key="code"
                    :headerTitle="
                      t('reports.fields.conceptsProportionalDeductions')
                    "
                    :required="true"
                    :options="itemsDeductions"
                    :error="errors.proportionalDeductions"
                    @change="filterCodeDeductions()" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.conceptsIRPF')"
                    v-model="withholdingsIRPF"
                    value-key="code"
                    :headerTitle="t('reports.fields.conceptsIRPF')"
                    :required="true"
                    :options="itemsWithholdings"
                    :error="errors.withholdingsIRPF"
                    @change="filterCodeWithholdings()" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.conceptsVacationSalary')"
                    v-model="vacationSalary"
                    value-key="code"
                    :headerTitle="t('reports.fields.conceptsVacationSalary')"
                    :required="true"
                    :options="itemsVacation"
                    :error="errors.vacationSalary"
                    @change="filterCodeVacation()" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.conceptsBonus')"
                    v-model="bonuses"
                    value-key="code"
                    :headerTitle="t('reports.fields.conceptsBonus')"
                    :required="true"
                    :options="itemsBonus"
                    :error="errors.bonuses"
                    @change="filterCodeBonus()" />
                </div>
              </div>
            </div>
            <div
              class="flex flex-col col-span-4 lg:col-span-1 border-0 border-t-2 lg:border-l-4 p-2 lg:border-t-0">
              <div class="pl-4 pr-4 lg:pr-0 flex justify-between">
                <h3
                  class="text-md font-medium leading-6 text-gray-900"
                  v-html="t('fields.employees')" />
                <h3
                  class="text-md font-medium leading-6 text-gray-900 flex items-center">
                  <DEmployeesFilter
                    :items="itemsEmployees"
                    v-model="employees" />
                  <FilterLinesIcon class="w-8" />
                </h3>
              </div>
              <div
                class="flex justify-center items-center p-5 sm:gap-4 lg:gap-3 xl:gap-4">
                <Users01Icon class="w-20 h-20" />
                <h1 class="text-5xl font-medium leading-6 text-gray-900">
                  {{
                    `${
                      employees?.length === undefined ? 0 : employees?.length
                    }/${itemsEmployees?.length}`
                  }}
                </h1>
              </div>
              <div class="text-xs text-red-500 self-center">
                {{ errors.employees }}
              </div>
            </div>
          </div>
          <template v-slot:footer>
            <div class="flex justify-end gap-2">
              <DBtn
                type="button"
                color="secondary"
                @click="back"
                class="px-5 py-2 font-semibold"
                v-html="t('form.button.back')" />
              <DBtn
                color="excel"
                class="px-5 py-2 font-semibold"
                @click.prevent="
                  onSubmit(),
                    (format =
                      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
                "
                v-html="t('form.button.excel')" />
              <!--
              <DBtn
                color="ods"
                class="px-5 py-2 font-semibold"
                @click.prevent="
                  onSubmit(),
                    (format = 'application/vnd.oasis.opendocument.spreadsheet')
                "
                v-html="'ODS'" />
                 -->
              <DBtn
                color="danger"
                class="px-5 py-2 font-semibold"
                @click.prevent="onSubmit(), (format = 'application/pdf')"
                v-html="t('form.button.pdf')" />
            </div>
          </template>
        </DCard>
      </div>
    </form>
  </div>
</template>
